import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Subject, Observable, filter } from 'rxjs';
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';

import { AuthService } from '../services/auth.service';
import { RealtimeEvent, RealtimeEventType } from './realtime-event.types';

/**
 * SignalR-Client für Live-Updates Kunde ↔ Handwerker.
 *
 * Lebenszyklus: hängt an <c>AuthService.currentUser$</c>. Sobald ein User
 * eingeloggt ist (Cookie vorhanden), wird die HubConnection aufgebaut; bei
 * Logout wieder abgerissen. Der Server schickt Events per
 * <c>NotificationReceived</c>; wir pushen die in den <c>events$</c>-Stream.
 *
 * SSR-Safe: Im Server-Rendering ist <c>isPlatformBrowser</c> = false, dann tut
 * der Service nichts. Connections sind WebSocket → kein Sinn auf dem Server.
 *
 * Auto-Reconnect: ja, mit exponential backoff. Komponenten brauchen sich nicht
 * um Wiederverbindung zu kümmern, sie abonnieren einfach <c>events$</c>.
 */
@Injectable({ providedIn: 'root' })
export class RealtimeService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly auth = inject(AuthService);

  private readonly _events$ = new Subject<RealtimeEvent>();
  /** Alle Live-Events. Filtern in Konsumenten via RxJS <c>filter()</c> auf <c>type</c>. */
  readonly events$: Observable<RealtimeEvent> = this._events$.asObservable();

  private connection: HubConnection | null = null;
  /** Trackt den User, für den die aktuelle Connection läuft — sonst doppelte Re-Init. */
  private connectedUserId: string | null = null;

  constructor() {
    if (!isPlatformBrowser(this.platformId)) return;

    // currentUser$ ist ein BehaviorSubject — wir bekommen den initialen Wert
    // sofort beim Subscribe. Kein Cleanup nötig: Service lebt root-providet,
    // also genauso lange wie die App.
    this.auth.currentUser$.subscribe((user) => {
      if (user) {
        void this.ensureConnected(user.userId);
      } else {
        void this.disconnect();
      }
    });
  }

  /**
   * Convenience: nur Events eines bestimmten Typs. Komponenten können sich so
   * gezielt auf z.B. <c>offer.submitted</c> abonnieren, ohne Boilerplate.
   */
  on(type: RealtimeEventType): Observable<RealtimeEvent> {
    return this.events$.pipe(filter((e) => e.type === type));
  }

  private async ensureConnected(userId: string): Promise<void> {
    if (this.connectedUserId === userId && this.connection?.state === HubConnectionState.Connected) {
      return;
    }

    // Falls eine alte Connection für einen anderen User existiert — sauber zumachen.
    await this.disconnect();

    const connection = new HubConnectionBuilder()
      .withUrl('/hubs/notifications', {
        // Cookie-Auth: SignalR nutzt fetch/EventSource/WebSocket; Browser
        // schickt das httpOnly-Identity-Cookie automatisch mit, weil same-origin.
        withCredentials: true,
      })
      // Backoff: 0s, 2s, 5s, 10s, dann 30s. Reicht für kurze Aussetzer und
      // belastet das Backend nicht bei längeren Server-Ausfällen.
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .configureLogging(LogLevel.Warning)
      .build();

    // Server-Methode (siehe SignalRRealtimeNotifier.ClientMethod).
    connection.on('NotificationReceived', (evt: RealtimeEvent) => {
      this._events$.next(evt);
    });

    this.connection = connection;
    this.connectedUserId = userId;

    try {
      await connection.start();
    } catch (err) {
      // Verbindungsaufbau fehlgeschlagen — kein Drama. Komponenten pollen
      // weiterhin alle 60s als Fallback, und SignalR versucht's beim nächsten
      // Auth-Tick erneut. Warning statt Error, damit die Console nicht rot wird.
      console.warn('[RealtimeService] HubConnection.start fehlgeschlagen:', err);
      this.connection = null;
      this.connectedUserId = null;
    }
  }

  private async disconnect(): Promise<void> {
    if (!this.connection) {
      this.connectedUserId = null;
      return;
    }

    try {
      await this.connection.stop();
    } catch {
      // Bewusst geschluckt: Stop kann fehlschlagen, wenn die Connection schon
      // tot ist — das ist Lifecycle, kein Bug.
    }

    this.connection = null;
    this.connectedUserId = null;
  }
}
